<script setup lang="ts">
import { storeToRefs } from 'pinia'

import InfluencerTagsSelectPreferences from '~/components/influencer/tags/selectPreferences.vue'
import { Base600Orange, Sm600Orange } from '~/components/typography'
import TitleOrangeBar from '~/components/ui/Title/OrangeBar.vue'
import VText from '~/components/ui/VText.vue'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserInfluencerTagsStore } from '~/stores/userInfluencerTags'

import type TagTarget from '~/types/tagTarget'

type Props = {
  exclusivityOnly?: boolean
  genresOnly?: boolean
}

type Emits = {
  'confirm-modifications': [
    tagData: {
      target: TagTarget
      index: number
    },
  ]
  'request-done': []
  'back-button-clicked': []
}

const props = withDefaults(defineProps<Props>(), {
  exclusivityOnly: false,
  genresOnly: false,
})
const emit = defineEmits<Emits>()

const configUtilityKeys: {
  readonly title: string
  readonly subtitle?: string
}[] = props.genresOnly
  ? [{ title: 'liked' }, { title: 'neutral' }, { title: 'hated' }]
  : props.exclusivityOnly
    ? [
        { title: 'exclusivity_lang' },
        { title: 'exclusivity_country' },
        { title: 'exclusivity_track_age' },
      ]
    : [
        { title: 'liked' },
        { title: 'neutral' },
        { title: 'hated' },
        {
          title: 'exclusivity_lang_and_country',
        },
        {
          title: 'exclusivity_track_age',
          subtitle: 'exclusivity_track_age',
        },
      ]

const expandedIndex = ref(0)

const userInfluencerTagsStore = useUserInfluencerTagsStore()
const { UPDATE_FROM_PATCH: UPDATE_INFLUENCER_TAGS_FROM_PATCH } =
  userInfluencerTagsStore

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const annotationComponent = computed<typeof Sm600Orange | typeof Base600Orange>(
  () => (SCREEN_WIDTH.value < 768 ? Sm600Orange : Base600Orange),
)

function handleConfirmModifications(data: {
  target: TagTarget
  index: number
}): void {
  expandedIndex.value++
  emit('confirm-modifications', data)
}

async function handleScrollBackToTop() {
  await nextTick()

  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  })
}

async function handleFinalizeSelection(): Promise<void> {
  const { liked, hated, neutral, exclusivity } = userInfluencerTagsStore.$state

  await UPDATE_INFLUENCER_TAGS_FROM_PATCH({
    patch: {
      liked,
      neutral,
      hated,
      exclusivity,
    },
  })
  emit('request-done')
}

function handleBackButtonClick() {
  if (expandedIndex.value) {
    expandedIndex.value--
    return
  }
  emit('back-button-clicked')
}
</script>

<template>
  <InfluencerTagsSelectPreferences
    :expanded-index="expandedIndex"
    :exclusivity-only="exclusivityOnly"
    :genres-only="genresOnly"
    @confirm-modifications="handleConfirmModifications"
    @finalize-selection="handleFinalizeSelection"
    @container-opening-done="handleScrollBackToTop"
    @need-scroll-up="handleScrollBackToTop"
    @handle-hollow-main-btn-click="handleBackButtonClick"
  >
    <template #default="slotProps">
      <div>
        <TitleOrangeBar
          class="title"
          light
          data-test-id="curatorSignupMusicPreferenceTags"
          :class="{ expand: expandedIndex === slotProps.index }"
        >
          <template #number>{{ slotProps.index + 1 }}</template>
          <template #title>
            <div class="titleWrap">
              <span>
                {{
                  $t(
                    `influencer.tags.selectPreferences.title.${
                      configUtilityKeys[slotProps.index].title
                    }`,
                  )
                }}
              </span>
              <component
                :is="annotationComponent"
                v-if="expandedIndex > slotProps.index"
                class="modBtn"
                @click="expandedIndex = slotProps.index"
              >
                {{ $t('common.edit') }}
              </component>
            </div>
          </template>
        </TitleOrangeBar>
        <VText
          v-if="
            configUtilityKeys[slotProps.index].subtitle &&
            expandedIndex === slotProps.index
          "
          cfg="sans/16/medium"
          class="tw-mb-4 tw-block"
        >
          {{
            $t(
              `influencer.tags.selectPreferences.subtitle.${
                configUtilityKeys[slotProps.index].subtitle
              }`,
            )
          }}
        </VText>
      </div>
    </template>
    <template #hollow-main-btn-text>
      <slot name="back-button-text" :expanded-index="expandedIndex"></slot>
    </template>
  </InfluencerTagsSelectPreferences>
</template>

<style scoped lang="scss">
.title:not(.expand) {
  @apply tw-mb-0;
}

.titleWrap {
  @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4;
}

.modBtn {
  @apply tw-cursor-pointer;
}
</style>
